import {
  ApiSmpArtGallery,
  IApiEntitySmpAgTag,
  IApiSmpAgTranslation,
} from "@smp/api";
import { Await } from "@smp/utils";
import { useMemo } from "react";
import { useInfiniteQuery } from "react-query";
import { UseInfiniteQueryOptions } from "react-query/types/react/types";
import { useUpdateEffect } from "react-use";

const KEY_POLOTNO_TAGS = "polotno.tags";

export const useQueryPolotnoTagsAll = (
  options?: Omit<
    UseInfiniteQueryOptions<
      Await<ReturnType<typeof ApiSmpArtGallery.Polotno.tags>>
    >,
    "queryKey" | "queryFn" | "getNextPageParam"
  >,
) => {
  const resp = useInfiniteQuery<
    Await<ReturnType<typeof ApiSmpArtGallery.Polotno.tags>>
  >(
    KEY_POLOTNO_TAGS,
    ({ pageParam }) =>
      ApiSmpArtGallery.Polotno.tags({
        ...pageParam,
        page_size: 100,
      }),
    {
      ...options,
      getNextPageParam: (response) =>
        response.next
          ? Object.fromEntries(new URL(response.next).searchParams)
          : undefined,
    },
  );

  const isFetched = useMemo(
    () => resp.isFetched && !resp.hasNextPage,
    [resp.hasNextPage, resp.isFetched],
  );

  const isLoading = useMemo(
    () =>
      resp.status === "loading" || (resp.status === "success" && !isFetched),
    [isFetched, resp.status],
  );

  const grouped = useMemo(() => {
    if (!isFetched || !resp.data) {
      return undefined;
    }

    const result: Map<
      string,
      {
        name: IApiSmpAgTranslation;
        slug: string;
        children: IApiEntitySmpAgTag[];
      }
    > = new Map();

    resp.data?.pages.forEach(({ results }) => {
      Object.values(results).forEach((tag) => {
        if (!tag.parent) {
          const group = result.get(tag.slug);

          if (!group) {
            result.set(tag.slug, {
              slug: tag.slug,
              name: tag.translations,
              children: [],
            });
          } else if (!Object.keys(group.name).length) {
            group.name = tag.translations;
          }
        } else {
          if (result.has(tag.parent)) {
            result.get(tag.parent)?.children.push(tag);
          } else {
            result.set(tag.parent, {
              slug: tag.parent,
              name: {},
              children: [tag],
            });
          }
        }
      });
    });

    return result;
  }, [isFetched, resp.data]);

  const normalized = useMemo(() => {
    if (!isFetched || !resp.data) {
      return undefined;
    }

    const result: Map<string, IApiEntitySmpAgTag> = new Map();

    resp.data?.pages.forEach(({ results }) => {
      Object.values(results).forEach((tag) => {
        result.set(tag.slug, tag);
      });
    });

    return result;
  }, [isFetched, resp.data]);

  const array = useMemo(() => {
    if (!isFetched || !resp.data) {
      return undefined;
    }

    return resp.data.pages.flatMap(({ results }) => Object.values(results));
  }, [isFetched, resp.data]);

  useUpdateEffect(() => {
    if (!resp.isFetching && resp.hasNextPage) {
      resp.fetchNextPage();
    }
  }, [resp.isFetching, resp.hasNextPage]);

  return {
    data: {
      array,
      grouped,
      normalized,
    },
    isFetched,
    isLoading,
  };
};

useQueryPolotnoTagsAll.KEY = KEY_POLOTNO_TAGS;
